import React, { useEffect, useState, useMemo } from "react";
import StudentWalletTable from "./table";
import { FaPlusCircle, FaLock, FaWallet } from "react-icons/fa";
import TableSkeleton from "../../../components/skeleton/table";
import CardComponent from "../../../components/card";
import CardSkeleton from "../../../components/skeleton/card";
import PaginationComponent from "../../../components/pagination";
import api from "../../../services/api";

const StudentWalletSummary = ({ id }) => {
  const [balanceDetails, setBalanceDetails] = useState({});
  const [loading, setLoading] = useState({});
  const [activeTab, setActiveTab] = useState("inbound");

  // Cache tab data by page
  const [tabData, setTabData] = useState({
    inbound: {},
    outbound: {},
    merge: {},
  });

  const [pageNos, setPageNos] = useState({
    inbound: 1,
    outbound: 1,
    merge: 1,
  });

  const [totalPages, setTotalPages] = useState({
    inbound: 1,
    outbound: 1,
    merge: 1,
  });

  const setApiLoading = (key, value) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const fetchTransactions = async (type, page) => {
    if (tabData[type][page]) return; // Already fetched

    const apiType = type === "merge" ? "inoutbound" : type;
    const key = `${type}_${page}`;

    try {
      setApiLoading(key, true);
      const { data } = await api.get(
        `/students/${id}/transactions?type=${apiType}&page=${page}`
      );

      const result = data?.data || [];

      setTabData((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          [page]: result,
        },
      }));

      setTotalPages((prev) => ({
        ...prev,
        [type]: data?.totalPages || 1,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setApiLoading(key, false);
    }
  };

  const fetchStudentBalance = async () => {
    try {
      setApiLoading("balance", true);
      const { data } = await api.get(
        `/students/${id}/balance`
      );
      setBalanceDetails(data?.data || {});
    } catch (error) {
      console.error(error);
    } finally {
      setApiLoading("balance", false);
    }
  };

  useEffect(() => {
    fetchStudentBalance();
  }, []);

  useEffect(() => {
    const page = pageNos[activeTab];
    if (id) {
      fetchTransactions(activeTab, page);
    }
  }, [activeTab, pageNos[activeTab], id]);

  const handlePageChange = (page) => {
    setPageNos((prev) => ({
      ...prev,
      [activeTab]: page,
    }));
  };

  const currentPage = pageNos[activeTab];
  const isLoading = loading[`${activeTab}_${currentPage}`];
  const currentData = tabData[activeTab]?.[currentPage] || [];

  const grandTotal = useMemo(() => {
    return Object.values(tabData.merge || {})
      .flat()
      .reduce((acc, item) => acc + parseFloat(item?.amount || 0), 0);
  }, [tabData.merge]);

  const stats = [
    {
      icon: <FaPlusCircle size={30} color="#1890ff" />,
      number: `${grandTotal} PKR`,
      label: "Topups",
    },
    {
      icon: <FaLock size={30} color="#722ed1" />,
      number: `${balanceDetails?.lockedBalance || 0} PKR`,
      label: "Locked Balance",
    },
    {
      icon: <FaWallet size={30} color="#52c41a" />,
      number: `${balanceDetails?.balance || 0} PKR`,
      label: "Balance",
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return <TableSkeleton columns={5} rows={4} />;
    }

    return (
      <StudentWalletTable transactionsData={currentData} tab={activeTab} />
    );
  };

  return (
    <div>
      {balanceDetails && Object.keys(balanceDetails).length > 0 ? (
        <CardComponent data={stats} />
      ) : (
        <CardSkeleton loading={loading["balance"]} boxes={3} />
      )}

      <div className="mt-4 col-12 mx-auto shadow border rounded px-2 py-2">
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "inbound" ? "active" : ""}`}
              onClick={() => setActiveTab("inbound")}
            >
              Topups
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "outbound" ? "active" : ""}`}
              onClick={() => setActiveTab("outbound")}
            >
              Purchase's
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "merge" ? "active" : ""}`}
              onClick={() => setActiveTab("merge")}
            >
              Transaction's History
            </button>
          </li>
        </ul>

        <div className="tab-content">
          <div className="table-responsive">{renderContent()}</div>
        </div>

        <div className="text-end">
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages[activeTab]}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default StudentWalletSummary;
